import {
  Content,
  Heading,
  Stack,
  Text,
  Inline,
  InlineItem,
  Box,
} from '@kinesis/bungle';
import { Chart as DirectChart, registerables } from 'chart.js';
import { SankeyController, Flow } from 'chartjs-chart-sankey';
import { GpcAnalysis, Inventory } from '@/types';
import { Chart } from 'react-chartjs-2';
import { Layout } from '@/components/layout';

import { EvaluationCard } from './evaluation-card';
import { EmissionsBySectorSource } from '@/components/emissions-by-sector-source';
import { GpcSummaryTable } from '@/components/gpc-summary-table';
import { GpcDetailTable } from '@/components/gpc-detail-table';
DirectChart.register(SankeyController, Flow, ...registerables);
import { colorCodes } from '@/data/colors';

const getHover = (key: string): any => colorCodes[key as any] as any;
const getColor = (key: string): any => colorCodes[key as any] as any;

const GpcReport = (props: GpcReportProps) => {
  const { inventory, gpc, enableBenchmark } = props;
  return (
    <Stack space='medium'>
      <Layout>
        <Heading as='h2' size='large'>
          Community Emissions Report
        </Heading>

        <Stack space='small'>
          <Heading as='h3' size='medium'>
            Introduction
          </Heading>

          <Text>
            The Local Emissions Data Platform (LEDP) is a collection of Regional
            and District councils that are collaborating on a shared approach to
            community emissions data collection, emissions inventory assembly,
            and net-zero pathway development. The collective is committed to
            deliver for all participants:
          </Text>
          <ul>
            <li>An up to date understanding of their community emissions</li>
            <li>
              The ability to report and communicate with your communities
              clearly about the sources of emissions
            </li>
            <li>Reconciled and tracked emissions reduction forecasts</li>
            <li>
              An evidence based testing environment for strategies around
              effective emission reduction initiatives
            </li>
          </ul>
          <Text>
            The partnership is coordinated by RSHL and supported by Kinesis
            through the provision of the Kinesis Platform.
          </Text>
          <Text>
            Kinesis Platform is a web-based data analytics platform, enabling
            cities and regions to harness the power of increasingly ubiquitous
            datasets to:
          </Text>
          <ul>
            <li>
              Collect, store and integrate disparate data sets from various
              sources.
            </li>
            <li>
              Increase efficiency in data sharing, decision making and
              reporting.
            </li>
            <li>
              Compare data sets across sectors and/or geographies to provide
              greater insight into urban and regional performance and identify
              emissions reduction opportunities.
            </li>
            <li>
              Communicate & report on regional and city emissions and
              performance.
            </li>
          </ul>

          <Text>
            This report documents the annual emissions inventory in line with
            the Global Protocol for Community Emissions Reporting (GPC).
          </Text>
          <Text>
            All data contained in this report has been extracted from LEDP. The
            LEDP methodology is documented here. The LEDP methodology is focused
            on the collection of local data reconciled at both regional and
            national levels where possible. The partnership is committed to
            delivering continuous improvement to the methodology.
          </Text>
          <Text>
            The long-term aim is to improve granularity of data from providers
            to improve the accuracy of sub-sector and suburb level data. Beyond
            the existing documented methodology the LEDP has established a
            process for the identification and collection of better and more
            timely data sources.
          </Text>
        </Stack>

        <Stack space='small'>
          <Heading as='h3' size='medium'>
            Your Community Inventory
          </Heading>

          <Heading as='h4' size='small'>
            Community Emissions for{' '}
            {{ FY24: '2023/24', CY24: '2024', CY23: '2023' }[
              inventory.defaultYear
            ] ?? '2023/24'}
          </Heading>
          <Text>
            The LEDP Community Emissions inventories are aiming for a BASIC+
            level of completeness. BASIC+ is a standardised reporting level and
            emissions boundary within the GPC framework that covers emissions
            from stationary energy, transportation, waste, Industrial Processes
            and Product Use (IPPU), Agriculture, Forestry and Other Land Use
            (AFOLU), and any other scope 3 emissions. Please note: while
            forestry-related emissions are included in LEDP, these emissions
            have been excluded from the GPC report outputs.
          </Text>
          <Stack space='large'>
            <Inline space='medium'>
              <InlineItem sizing='fill-container'>
                <EvaluationCard
                  background='yellow'
                  // eslint-disable-next-line max-len
                  title={`Community Emissions for ${{ FY24: '2023/24', CY24: '2024', CY23: '2023' }[inventory.defaultYear] ?? '2023/24'}`}
                  value={gpc.grossEmissions}
                  unit={{ type: 'kt-co2e' }}
                  // eslint-disable-next-line max-len
                  description='Total gross emissions across sectors and activity sources.'
                />
              </InlineItem>
              <InlineItem sizing='fill-container'>
                <EvaluationCard
                  inlineUnit
                  inlineUnitDescription='percentage of total emissions'
                  background='blue'
                  title={`${gpc.focalSource.label} Source Highlight`}
                  value={gpc.focalSource.percentage}
                  unit={{ type: 'percentage' }}
                  description={`${gpc.focalSource.label} is the largest contributing source of emissions`}
                />
              </InlineItem>
              <InlineItem sizing='fill-container'>
                <EvaluationCard
                  inlineUnit
                  inlineUnitDescription='percentage of total emissions'
                  background='green'
                  title={`${gpc.focalSector.label} Sector Highlight`}
                  value={gpc.focalSector.percentage}
                  unit={{ type: 'percentage' }}
                  description={`${gpc.focalSector.label} is the largest contributing sector of emissions`}
                />
              </InlineItem>
            </Inline>

            <EmissionsBySectorSource data={gpc.bySectorSource} />
          </Stack>
        </Stack>
      </Layout>

      <Stack space='small'>
        <Layout>
          <Heading as='h4' size='small'>
            GPC Reporting Table
          </Heading>
        </Layout>
        <Stack space='large'>
          <Layout>
            <GpcSummaryTable data={gpc.summaryTable} />
            <Text tone='secondary'>
              Note: forestry related emissions are currently excluded from the
              GPC report.
            </Text>
          </Layout>
          <Content padding='medium'>
            <GpcDetailTable data={gpc.detailTable} />
          </Content>
        </Stack>
      </Stack>

      <Layout>
        <Stack space='small'>
          <Heading as='h4' size='small'>
            Extrapolating Reporting Data for City + Regional Action
          </Heading>
          <Text>
            What the GPC reporting framework does is allocate emissions data
            into a number of sectors and sources so that a clear and aggregate
            account of all emissions is accounted for and documented. What it
            doesn't do is reconcile those emission sources and sectors with how
            we understand cities, regions and communities. The LEDP
            collaboration takes that next step.
          </Text>
          <Text>
            The LEDP has captured and stored source data in a way that it can be
            transformed into sectors and geographies that identify with public
            policies relating to regional and urban management. With the
            inclusion of other publicly available data sets, LEDP helps identify
            and prioritise the key emissions activities, sectors, sub-sectors
            and areas for decarbonisation. For each city and region, and even
            within regions these decarbonisation opportunities may vary but they
            are able to be translated to individual policies and programs, for
            instance:
          </Text>
          <ul>
            <li>
              The impact of new building standards on individual typologies
            </li>
            <li>
              Optimising locations of EV charging stations for residential
              vehicle use
            </li>
            <li>Impact of waste minimisation strategies across sectors</li>
          </ul>
          <Text>
            LEDP users can model these opportunities through Kinesis for
            Communities.
          </Text>
          <Stack space='large'>
            <Chart
              type='sankey'
              options={{
                aspectRatio: 1.5,
              }}
              data={
                {
                  datasets: [
                    {
                      label:
                        'Community Emissions Activity and Sector Breakdown',
                      data: gpc.sankey.flows.map((v) => ({
                        from: v.from,
                        to: v.to,
                        flow: v.value,
                      })),
                      // chartjs-chart-sankey resolves these once for
                      // `dataIndex: 0` even when `data` is empty (see
                      // updateElements in chartjs-chart-sankey), so they have
                      // to tolerate a missing data entry — otherwise an empty
                      // sankey throws TypeError at render time.
                      colorFrom: (c: any) =>
                        getColor(c.dataset.data[c.dataIndex]?.from),
                      colorTo: (c: any) =>
                        getColor(c.dataset.data[c.dataIndex]?.to),
                      hoverColorFrom: (c: any) =>
                        getHover(c.dataset.data[c.dataIndex]?.from),
                      hoverColorTo: (c: any) =>
                        getHover(c.dataset.data[c.dataIndex]?.to),
                      colorMode: 'gradient', // or 'from' or 'to'
                      /* optionally override default alpha (0.5) applied to colorFrom and colorTo */
                      alpha: 0.5,
                      /* optional labels */
                      labels: gpc.sankey.labels,
                      /* optional priority */
                      priority: {
                        b: 1,
                        d: 0,
                      },
                      /* optional column overrides */
                      column: {
                        d: 1,
                      },
                      size: 'max', // or 'min' if flow overlap is preferred
                    },
                  ],
                } as any
              }
            />
          </Stack>
        </Stack>
      </Layout>
      {enableBenchmark && (
        <Layout>
          <Stack space='small'>
            <Heading as='h4' size='small'>
              Benchmarking Performance
            </Heading>
            <Text>
              LEDP delivers a consistent approach for community emissions
              inventories for all regions of New Zealand. This approach enables
              two outcomes:
            </Text>
            <ol>
              <li>Consistency in methodology</li>
              <li>
                Completeness and consistency with New Zealand’s national
                inventory
              </li>
              <li>
                Comparative benchmarking helping Councils compare and
                collaborate on community emissions reductions
              </li>
            </ol>

            <Text>
              For{' '}
              {{ FY24: '2023/24', CY24: '2024', CY23: '2023' }[
                inventory.defaultYear
              ] ?? '2023/24'}
              , several comparisons are outlined below:
            </Text>

            <Stack space='large'>
              <Box height={240} />
            </Stack>

            <Stack space='large'>
              <Box height={240} />
            </Stack>
          </Stack>
        </Layout>
      )}
    </Stack>
  );
};

export type GpcReportProps = {
  now: string;
  enableBenchmark?: boolean;
  gpc: GpcAnalysis;
  inventory: Inventory;
};

export { GpcReport };
